import { AbstractControl, UntypedFormArray, UntypedFormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { CONSTANT } from "src/app/constant";
import { CandidateRole } from "src/app/models/enum";
import {
	CandidateRating,
	DraftItem,
	ManeuverOutcomeDraft,
	OBAttempt,
	OBComments,
	OBCommentsReq,
	ResetManeuverItem,
} from "src/app/models/models";
import { CbtaModalComponent } from "src/app/shared/components/cbta-modal/cbta-modal.component";
import { ContainerRatingsService } from "../../services/base/container-ratings.services";
import {
	appendObCommentToSummary,
	coerceBool,
	computeObAttemptAfterUndoOneStep,
	crewRoleToObSlot,
	filterSelectedOBsForSummary,
	forEachManeuverRoleObCell,
	getUndoableObEventsForRole,
	lastUserObLastAction,
	maneuverHasRepeatSummaryHistory,
	obCanUndoOneStep,
	obDraftStoreKeyForRole,
	obEventMatchesRole,
	ObRoleSlot,
	parseObsCode,
	resolveUndoCommentCandidateRole,
	restoreManeuverFormRoleFromSnapshot,
} from "../../shared-v2/utils/observation.utils";
import { collectAssessmentConfigObPresets } from "./ts1-predefined-ob.utils";
import { rebuildDisplaySnapshots } from "./ts1-rating-display-snapshot.utils";
import {
	buildLatestActiveOutcomeIndexMap,
	isReplaceableActiveOutcomeRow,
	OutcomeSlot,
	outcomeSlotCandidateRole,
	outcomeSlotForObColumn,
	outcomeUpsertKey,
} from "./ts1-rating-outcome-interaction";
import { isCompetencyManeuverCode } from "./ts1-rating-review.utils";
import { withTs1SummaryManeuverLabel } from "./ts1-summary-display.utils";

export const DEFAULT_OB_COMMENTS_ENABLED = true;

/** Candidate Rating "Enable Comment" switch. Typed as boolean so both branches stay live. */
export const SHOW_ENABLE_COMMENT_TOGGLE: boolean = true;

export interface Ts1RatingObContext {
	maneuverForm: UntypedFormGroup;
	obLabel: string;
	candidateRole: CandidateRole;
	/** Display name for the OB column candidate (shown in comment modal header). */
	candidateName?: string;
	dialog: MatDialog;
	containerRatingsService: ContainerRatingsService;
	onCommentEmitted?: (comment: OBComments) => void;
	onDraftUpdated?: () => void;
	initialObByKey?: Record<string, OBAttempt>;
	/** When true, OB +/- opens comment modal; when false/omitted, auto-captures with empty comment. */
	obCommentsEnabled?: boolean;
}

export function candidateRoleDisplayLabel(role: CandidateRole | undefined): string {
	if (role === CandidateRole.PF) return "Pilot Flying";
	if (role === CandidateRole.PM) return "Pilot Monitoring";
	return "";
}

function buildObCommentModalNewComment(
	ctx: Ts1RatingObContext,
	action: boolean | null,
	isObservationComment?: boolean,
): {
	maneuver: string;
	ob: string;
	action: boolean | null;
	isObservationComment?: boolean;
	candidateName?: string;
	candidateRoleLabel?: string;
	candidateRole?: CandidateRole;
} {
	const candidateName = ctx.candidateName?.trim() || undefined;
	const candidateRoleLabel = candidateRoleDisplayLabel(ctx.candidateRole) || undefined;
	return {
		maneuver: maneuverLabel(ctx.maneuverForm),
		ob: ctx.obLabel,
		action,
		...(isObservationComment ? { isObservationComment: true } : {}),
		...(candidateName ? { candidateName } : {}),
		...(candidateRoleLabel ? { candidateRoleLabel, candidateRole: ctx.candidateRole } : {}),
	};
}

export function getSafeCandidateRating(record?: DraftItem): CandidateRating {
	const cr = record?.candidateRating;
	return {
		temEvaluations: cr?.temEvaluations ?? [],
		temRate: cr?.temRate ?? {},
		addRemoveOb: cr?.addRemoveOb ?? [],
		otherComments: cr?.otherComments ?? [],
		resetManeuvers: cr?.resetManeuvers ?? [],
		selectedOBs: cr?.selectedOBs ?? [],
		crewRoleAssignment: cr?.crewRoleAssignment,
		maneuverOutcomes: cr?.maneuverOutcomes ?? [],
		temCompetencyOutcomes: cr?.temCompetencyOutcomes ?? [],
		obCommentsEnabled: cr?.obCommentsEnabled ?? DEFAULT_OB_COMMENTS_ENABLED,
		displayComments: cr?.displayComments ?? [],
		displayObs: cr?.displayObs ?? [],
	};
}

export function buildObservationCode(formattedOB: string): string {
	const spaceIdx = formattedOB.indexOf(" ");
	if (spaceIdx >= 0) {
		return `${formattedOB.slice(0, spaceIdx)}_${formattedOB.slice(spaceIdx + 1)}`;
	}
	const match = /^([a-z]+)(\d+)$/.exec(formattedOB);
	if (match) {
		return `${match[1]}_${match[2]}`;
	}
	return formattedOB;
}

function maneuverLabel(maneuverForm: UntypedFormGroup): string {
	const maneuver = maneuverForm.get("maneuver")?.value ?? "";
	return maneuver.toLowerCase() !== CONSTANT.DUMMY_PLACEHOLDER.toLowerCase() ? maneuver : "";
}

export function computeObAddValue(current: OBAttempt): OBAttempt | null {
	if (current.totalCount >= CONSTANT.MAX_OB_ATTEMPT_LIMIT) return null;
	return {
		...current,
		totalCount: current.totalCount + 1,
		currentCount: current.currentCount + 1,
		lastAction: CONSTANT.OB_LAST_ACTION.ADDED,
	};
}

export function computeObRemoveValue(current: OBAttempt): OBAttempt | null {
	let { currentCount, totalCount } = current;
	let last = current.lastAction ?? CONSTANT.OB_LAST_ACTION.NO_ACTION;

	if (current.preDefined) {
		if (currentCount > 0) {
			currentCount--;
			last = CONSTANT.OB_LAST_ACTION.REMOVED;
		} else if (totalCount < CONSTANT.MAX_OB_ATTEMPT_LIMIT) {
			totalCount++;
			last = CONSTANT.OB_LAST_ACTION.REMOVED;
		} else {
			return null;
		}
	} else {
		if (currentCount > 0) {
			if (totalCount < CONSTANT.MAX_OB_ATTEMPT_LIMIT) {
				totalCount++;
			} else {
				currentCount--;
			}
			last = CONSTANT.OB_LAST_ACTION.REMOVED;
		} else if (totalCount < CONSTANT.MAX_OB_ATTEMPT_LIMIT) {
			totalCount++;
			last = CONSTANT.OB_LAST_ACTION.REMOVED;
		} else {
			return null;
		}
	}

	return { ...current, totalCount, currentCount, lastAction: last };
}

function syncManeuverSelectedOBs(ctx: Ts1RatingObContext, selectedOBs: OBComments[]): void {
	const candidateId = ctx.maneuverForm.get("candidateId")?.value;
	const maneuverCode = ctx.maneuverForm.get("maneuverCode")?.value;
	if (!candidateId || !maneuverCode) return;

	const maneuverSummaryRows = filterSelectedOBsForSummary(selectedOBs, candidateId).filter(
		(row) => row.maneuverCode === maneuverCode,
	);
	ctx.maneuverForm.get("selectedOBs")?.patchValue(maneuverSummaryRows, { emitEvent: false });
}

function resolveNaCandidateRole(
	ctx: Ts1RatingObContext,
	observationCode: string,
	selectedOBs: OBComments[],
): CandidateRole {
	if (ctx.candidateRole && ctx.candidateRole !== CandidateRole.NONE) {
		return ctx.candidateRole;
	}

	const matching = selectedOBs.filter((row) => row.observationCode === observationCode || row.ob === ctx.obLabel);
	for (let i = matching.length - 1; i >= 0; i--) {
		const role = matching[i].candidateRole;
		if (role && role !== CandidateRole.NONE) {
			return role;
		}
	}

	// ctx.candidateRole is missing or NONE here — never return null/undefined.
	return CandidateRole.PF;
}

function persistObDraft(
	ctx: Ts1RatingObContext,
	control: AbstractControl,
	obLabel: string,
	isAdded: boolean,
	newValue: OBAttempt,
	comment: string,
): void {
	const record = ctx.containerRatingsService.draft;
	const cr = getSafeCandidateRating(record);
	const formattedOB = obLabel.toLowerCase();
	const maneuverCode = ctx.maneuverForm.get("maneuverCode")?.value;
	const candidateId = ctx.maneuverForm.get("candidateId")?.value;
	const mLabel = maneuverLabel(ctx.maneuverForm);

	const createdAt = new Date().toISOString();

	const obAction: OBComments = {
		maneuver: mLabel,
		ob: obLabel,
		action: isAdded,
		instructorComment: comment.trim().toUpperCase(),
		commentId: "",
		maneuverCode,
		observationCode: buildObservationCode(formattedOB),
		candidateId,
		candidateRole: ctx.candidateRole,
		isDisabled: false,
		createdAt,
	};

	const obActionReq: OBCommentsReq = {
		candidateId,
		observationCode: buildObservationCode(formattedOB),
		maneuverCode,
		isRemoved: !isAdded,
		description: comment.trim().toUpperCase(),
		activeAttempt: newValue.currentCount ?? 0,
		totalAttempt: newValue.totalCount ?? 0,
		candidateRole: ctx.candidateRole,
		isDisabled: false,
		createdAt,
	};

	control.patchValue(newValue);

	const pendingCandidateRating: CandidateRating = {
		...cr,
		addRemoveOb: [...cr.addRemoveOb, obActionReq],
		selectedOBs: [...cr.selectedOBs, obAction],
	};

	ctx.containerRatingsService.updateSelectedDraft({
		candidateRating: pendingCandidateRating,
		lastUpdatedDate: new Date().toISOString(),
		continuePage: 2,
	});

	syncManeuverSelectedOBs(ctx, pendingCandidateRating.selectedOBs);

	ctx.onCommentEmitted?.(obAction);
	ctx.onDraftUpdated?.();
}

export function obCanUndoOneStepForCell(ctx: Ts1RatingObContext): boolean {
	const candidateId = ctx.maneuverForm.get("candidateId")?.value;
	const maneuverCode = ctx.maneuverForm.get("maneuverCode")?.value;
	if (!candidateId || !maneuverCode) return false;

	const observationCode = buildObservationCode(ctx.obLabel.toLowerCase());
	const cr = getSafeCandidateRating(ctx.containerRatingsService.draft);
	const events = getUndoableObEventsForRole(
		cr.addRemoveOb,
		candidateId,
		maneuverCode,
		observationCode,
		cr.resetManeuvers,
		ctx.candidateRole,
	);
	return obCanUndoOneStep(events);
}

export function handleObReset(ctx: Ts1RatingObContext, control: AbstractControl, current: OBAttempt): void {
	const formattedOB = ctx.obLabel.toLowerCase();
	const observationCode = buildObservationCode(formattedOB);
	const candidateId = ctx.maneuverForm.get("candidateId")?.value;
	const maneuverCode = ctx.maneuverForm.get("maneuverCode")?.value;
	if (!candidateId || !maneuverCode) return;

	const record = ctx.containerRatingsService.draft;
	const cr = getSafeCandidateRating(record);
	const initialKey = obDraftStoreKeyForRole(candidateId, maneuverCode, observationCode, ctx.candidateRole);
	const initialOb = ctx.initialObByKey?.[initialKey];

	const undoableEvents = getUndoableObEventsForRole(
		cr.addRemoveOb,
		candidateId,
		maneuverCode,
		observationCode,
		cr.resetManeuvers,
		ctx.candidateRole,
	);
	if (undoableEvents.length === 0) return;

	const lastEvent = undoableEvents[undoableEvents.length - 1];
	const lastCreatedAt = lastEvent.createdAt;
	const restored = computeObAttemptAfterUndoOneStep(undoableEvents, initialOb);
	if (!restored) return;

	const updated: OBAttempt = {
		...restored,
		isDisabled: coerceBool(restored.isDisabled, false) ?? false,
		lastAction: restored.lastAction ?? CONSTANT.OB_LAST_ACTION.NO_ACTION,
	};
	control.patchValue(updated);

	let lastSelectedIdx = -1;
	for (let i = cr.selectedOBs.length - 1; i >= 0; i--) {
		const a = cr.selectedOBs[i];
		// Undo targets the +/- summary row only — standalone observation notes stay until edited/deleted.
		if (a.isObservationComment || a.isManeuverOutcome || a.isTemCompetencyOutcome) continue;
		if (
			a.candidateId === candidateId &&
			a.maneuverCode === maneuverCode &&
			a.observationCode === observationCode &&
			obEventMatchesRole(a, ctx.candidateRole) &&
			!(a.isRepeat ?? false) &&
			!a.isReset
		) {
			lastSelectedIdx = i;
			break;
		}
	}

	const undoCandidateRole = resolveUndoCommentCandidateRole(
		undoableEvents,
		cr.selectedOBs,
		candidateId,
		maneuverCode,
		observationCode,
		lastSelectedIdx,
	);

	const addRemoveOb = [
		...cr.addRemoveOb.map((a) =>
			a.candidateId === candidateId &&
			a.maneuverCode === maneuverCode &&
			a.observationCode === observationCode &&
			a.createdAt === lastCreatedAt &&
			obEventMatchesRole(a, ctx.candidateRole) &&
			!(a.isRepeat ?? false)
				? { ...a, isRepeat: true }
				: a,
		),
		{
			candidateId,
			observationCode,
			maneuverCode,
			isRemoved: null,
			isReset: true,
			description: CONSTANT.RESET_OB_NOTES,
			activeAttempt: updated.currentCount ?? 0,
			totalAttempt: updated.totalCount ?? 0,
			candidateRole: undoCandidateRole,
			isDisabled: false,
			createdAt: new Date().toISOString(),
		} as OBCommentsReq,
	];

	const selectedOBs =
		lastSelectedIdx >= 0 ? cr.selectedOBs.filter((_, i) => i !== lastSelectedIdx) : [...cr.selectedOBs];
	const pendingCandidateRating = { ...cr, addRemoveOb, selectedOBs };

	ctx.containerRatingsService.updateSelectedDraft({
		candidateRating: pendingCandidateRating,
		lastUpdatedDate: new Date().toISOString(),
		continuePage: 2,
	});

	syncManeuverSelectedOBs(ctx, pendingCandidateRating.selectedOBs);

	ctx.onDraftUpdated?.();
	ctx.onCommentEmitted?.({
		isReset: true,
		candidateId,
		maneuverCode,
		observationCode,
		ob: ctx.obLabel,
	} as OBComments);
}

export function handleObToggleNA(
	ctx: Ts1RatingObContext,
	control: AbstractControl,
	current: OBAttempt,
	disabled: boolean,
): void {
	const formattedOB = ctx.obLabel.toLowerCase();
	const observationCode = buildObservationCode(formattedOB);
	const candidateId = ctx.maneuverForm.get("candidateId")?.value;
	const maneuverCode = ctx.maneuverForm.get("maneuverCode")?.value;
	if (!candidateId || !maneuverCode) return;

	const cr = getSafeCandidateRating(ctx.containerRatingsService.draft);

	let lastNonDisabledAction: boolean | null = null;
	const naCandidateRole = resolveNaCandidateRole(ctx, observationCode, cr.selectedOBs);
	const lastActionOfSelectedObNotNull = cr.selectedOBs.filter(
		(observation) =>
			!observation.isRepeat &&
			!observation.isReset &&
			!observation.isObservationComment &&
			observation.candidateId === candidateId &&
			observation.maneuverCode === maneuverCode &&
			observation.action != null &&
			(observation.observationCode === observationCode || observation.ob === ctx.obLabel) &&
			(!observation.candidateRole ||
				observation.candidateRole === CandidateRole.NONE ||
				observation.candidateRole === naCandidateRole),
	);
	if (lastActionOfSelectedObNotNull.length > 0) {
		const last = lastActionOfSelectedObNotNull[lastActionOfSelectedObNotNull.length - 1];
		lastNonDisabledAction = last.action ?? null;
	}

	const updated: OBAttempt = {
		...current,
		isDisabled: disabled,
		lastAction: disabled
			? CONSTANT.OB_LAST_ACTION.DISABLED
			: (CONSTANT.OB_LAST_ACTION.RESTORE as OBAttempt["lastAction"]),
	};
	if (!disabled) {
		updated.lastAction =
			lastNonDisabledAction != null
				? lastNonDisabledAction
					? CONSTANT.OB_LAST_ACTION.ADDED
					: CONSTANT.OB_LAST_ACTION.REMOVED
				: CONSTANT.OB_LAST_ACTION.NO_ACTION;
	}

	control.patchValue(updated);

	const mLabel = maneuverLabel(ctx.maneuverForm);
	const createdAt = new Date().toISOString();
	const obAction: OBComments = {
		maneuver: mLabel,
		ob: ctx.obLabel,
		action: !disabled ? lastNonDisabledAction : null,
		instructorComment: "",
		commentId: "",
		maneuverCode,
		observationCode,
		candidateId,
		candidateRole: naCandidateRole,
		isDisabled: disabled,
		createdAt,
	};

	const obActionReq: OBCommentsReq = {
		candidateId,
		observationCode,
		maneuverCode,
		isRemoved: !disabled ? (lastNonDisabledAction != null ? !lastNonDisabledAction : null) : null,
		description: disabled ? CONSTANT.NOT_APPLICABLE_NOTES : CONSTANT.RESET_NOT_APPLICABLE_NOTES,
		activeAttempt: updated.currentCount ?? 0,
		totalAttempt: updated.totalCount ?? 0,
		candidateRole: naCandidateRole,
		isDisabled: disabled,
		createdAt,
	};

	const nextSelectedOBs = [...cr.selectedOBs];
	if (disabled) {
		nextSelectedOBs.push(obAction);
	} else {
		for (let i = nextSelectedOBs.length - 1; i >= 0; i--) {
			const row = nextSelectedOBs[i];
			if (
				row.maneuverCode === maneuverCode &&
				row.ob === "REPEAT" &&
				(!row.candidateId || row.candidateId === candidateId)
			) {
				break;
			}
			if (
				row.isRepeat ||
				row.isReset ||
				row.isObservationComment ||
				row.isManeuverOutcome ||
				row.isTemCompetencyOutcome ||
				!row.isDisabled
			) {
				continue;
			}
			if (row.candidateId !== candidateId || row.maneuverCode !== maneuverCode) continue;
			if (!obEventMatchesRole(row, naCandidateRole)) continue;
			if (row.observationCode === observationCode || row.ob === ctx.obLabel) {
				nextSelectedOBs.splice(i, 1);
				break;
			}
		}
	}

	const pendingCandidateRating: CandidateRating = {
		...cr,
		addRemoveOb: [...cr.addRemoveOb, obActionReq],
		selectedOBs: nextSelectedOBs,
	};

	ctx.containerRatingsService.updateSelectedDraft({
		candidateRating: pendingCandidateRating,
		lastUpdatedDate: new Date().toISOString(),
		continuePage: 2,
	});

	syncManeuverSelectedOBs(ctx, pendingCandidateRating.selectedOBs);

	ctx.onDraftUpdated?.();
	ctx.onCommentEmitted?.(
		disabled
			? obAction
			: ({
					isReset: true,
					lastActionDisabled: true,
					candidateId,
					maneuverCode,
					observationCode,
					ob: ctx.obLabel,
					candidateRole: naCandidateRole,
				} as OBComments),
	);
}

/** Snapshot all OB attempts for one role column (keyed by observationCode). */
export function captureRoleObSnapshot(form: UntypedFormGroup, roleSlot: ObRoleSlot): Record<string, OBAttempt> {
	const snapshot: Record<string, OBAttempt> = {};
	forEachManeuverRoleObCell(form, roleSlot, ({ observationCode, attempt }) => {
		snapshot[observationCode] = structuredClone(attempt);
	});
	return snapshot;
}

/**
 * Cascade true OB N/A for a Maneuver Outcome slot:
 * - snapshot prior attempts onto the outcome draft
 * - set each non-disabled cell isDisabled
 * Column N/A is the source of truth — do not fan out addRemoveOb rows.
 */
export function applyRoleObsNaForOutcome(params: {
	form: UntypedFormGroup;
	slot: OutcomeSlot;
	outcome: ManeuverOutcomeDraft;
	containerRatingsService: ContainerRatingsService;
}): ManeuverOutcomeDraft {
	const { form, slot } = params;
	const candidateRole = outcomeSlotCandidateRole(slot);
	const roleSlot = crewRoleToObSlot(candidateRole);
	const candidateId = form.get("candidateId")?.value as string | undefined;
	const maneuverCode = form.get("maneuverCode")?.value as string | undefined;
	if (!candidateId || !maneuverCode) return params.outcome;

	const existingSnapshots = { ...(params.outcome.preNaObSnapshots ?? {}) };
	if (!existingSnapshots[slot]) {
		existingSnapshots[slot] = captureRoleObSnapshot(form, roleSlot);
	}

	forEachManeuverRoleObCell(form, roleSlot, ({ control, attempt }) => {
		if (attempt.isDisabled) return;

		control.patchValue(
			{
				...attempt,
				isDisabled: true,
				lastAction: CONSTANT.OB_LAST_ACTION.DISABLED,
			},
			{ emitEvent: false },
		);
	});

	return {
		...params.outcome,
		preNaObSnapshots: existingSnapshots,
	};
}

/**
 * Restore role OBs from the pre-N/A snapshot. Column un-N/A does not write addRemoveOb.
 */
export function restoreRoleObsFromOutcomeNa(params: {
	form: UntypedFormGroup;
	slot: OutcomeSlot;
	outcome: ManeuverOutcomeDraft;
	containerRatingsService: ContainerRatingsService;
}): ManeuverOutcomeDraft {
	const { form, slot } = params;
	const candidateRole = outcomeSlotCandidateRole(slot);
	const roleSlot = crewRoleToObSlot(candidateRole);
	const candidateId = form.get("candidateId")?.value as string | undefined;
	const maneuverCode = form.get("maneuverCode")?.value as string | undefined;
	if (!candidateId || !maneuverCode) return params.outcome;

	const snapshot = params.outcome.preNaObSnapshots?.[slot] ?? {};
	const cr = getSafeCandidateRating(params.containerRatingsService.draft);
	const addRemoveOb = cr.addRemoveOb ?? [];
	const createdAt = new Date().toISOString();

	forEachManeuverRoleObCell(form, roleSlot, ({ observationCode, control, attempt }) => {
		const prior = snapshot[observationCode];
		if (!prior) {
			if (attempt.isDisabled) {
				control.patchValue(
					{
						...attempt,
						isDisabled: false,
						lastAction: lastUserObLastAction(
							addRemoveOb,
							{
								candidateId,
								maneuverCode,
								observationCode,
								candidateRole,
								createdAt,
							},
							CONSTANT.OB_LAST_ACTION.NO_ACTION,
						),
					},
					{ emitEvent: false },
				);
			}
			return;
		}

		control.patchValue(structuredClone(prior), { emitEvent: false });
	});

	const nextSnapshots = { ...(params.outcome.preNaObSnapshots ?? {}) };
	delete nextSnapshots[slot];

	return {
		...params.outcome,
		preNaObSnapshots: Object.keys(nextSnapshots).length > 0 ? nextSnapshots : undefined,
	};
}

const OUTCOME_NA_CASCADE_SLOTS: OutcomeSlot[] = ["pfPrimary", "pm", "pmPrimary", "pf"];

export function candidateFormIndexForOutcomeSlot(slot: OutcomeSlot): number {
	return slot === "pfPrimary" || slot === "pm" ? 0 : 1;
}

export function findManeuverFormForOutcomeSlot(
	ratingForm: UntypedFormArray | null | undefined,
	slot: OutcomeSlot,
	maneuverCode: string,
): UntypedFormGroup | null {
	if (!ratingForm?.length || !maneuverCode) return null;
	const index = candidateFormIndexForOutcomeSlot(slot);
	const obs = ratingForm.at(index)?.get("observations") as UntypedFormArray | null;
	if (!obs) return null;
	const found = obs.controls.find(
		(control) => (control as UntypedFormGroup).get("maneuverCode")?.value === maneuverCode,
	);
	return (found as UntypedFormGroup) ?? null;
}

/** After draft replay, disable columns whose Maneuver Outcome is N/A. */
export function applyManeuverOutcomeNaToObservationForms(
	observations: UntypedFormArray,
	maneuverOutcomes: ManeuverOutcomeDraft[] | undefined,
	formIndex: number,
	hasSecondCandidate: boolean,
): void {
	if (!observations?.length || !maneuverOutcomes?.length) return;
	const side: "left" | "right" = formIndex === 0 ? "left" : "right";

	for (let i = 0; i < observations.length; i++) {
		const form = observations.at(i) as UntypedFormGroup;
		const maneuverCode = form.get("maneuverCode")?.value as string | undefined;
		if (!maneuverCode) continue;
		const outcome = maneuverOutcomes.find((row) => row.maneuverCode === maneuverCode);
		if (!outcome) continue;

		for (const role of [CandidateRole.PF, CandidateRole.PM]) {
			const slot = outcomeSlotForObColumn(side, role, hasSecondCandidate);
			if (outcome[slot] !== "na") continue;
			const roleSlot = crewRoleToObSlot(role);
			forEachManeuverRoleObCell(form, roleSlot, ({ control, attempt }) => {
				if (attempt.isDisabled) return;
				control.patchValue(
					{
						...attempt,
						isDisabled: true,
						lastAction: CONSTANT.OB_LAST_ACTION.DISABLED,
					},
					{ emitEvent: false },
				);
			});
		}
	}
}

/**
 * When a Maneuver Outcome slot enters/leaves N/A, cascade that seat's OBs
 * (disable + addRemoveOb events, or restore from snapshot). Used by Step 2 and Step 3.
 */
export function syncManeuverOutcomeNaCascade(params: {
	previous?: ManeuverOutcomeDraft;
	outcome: ManeuverOutcomeDraft;
	containerRatingsService: ContainerRatingsService;
	formForSlot: (slot: OutcomeSlot) => UntypedFormGroup | null;
}): ManeuverOutcomeDraft {
	let nextOutcome = { ...params.outcome };
	for (const slot of OUTCOME_NA_CASCADE_SLOTS) {
		const prevVal = params.previous?.[slot];
		const nextVal = params.outcome[slot];
		if (prevVal === nextVal) continue;

		const form = params.formForSlot(slot);
		if (!form) continue;

		if (nextVal === "na" && prevVal !== "na") {
			nextOutcome = applyRoleObsNaForOutcome({
				form,
				slot,
				outcome: nextOutcome,
				containerRatingsService: params.containerRatingsService,
			});
		} else if (prevVal === "na" && nextVal !== "na") {
			nextOutcome = restoreRoleObsFromOutcomeNa({
				form,
				slot,
				outcome: {
					...nextOutcome,
					preNaObSnapshots: params.previous?.preNaObSnapshots ?? nextOutcome.preNaObSnapshots,
				},
				containerRatingsService: params.containerRatingsService,
			});
		}
	}
	return nextOutcome;
}

export function handleObAdd(ctx: Ts1RatingObContext, control: AbstractControl, current: OBAttempt): void {
	const next = computeObAddValue(current);
	if (!next) return;

	if (!ctx.obCommentsEnabled) {
		persistObDraft(ctx, control, ctx.obLabel, true, next, "");
		return;
	}

	const dialogRef = ctx.dialog.open(CbtaModalComponent, {
		panelClass: "ob-comments-modal",
		height: "auto",
		maxHeight: "90vh",
		width: "1154px",
		hasBackdrop: true,
		data: {
			modalName: "obCommentsModel",
			modalTitle: "Comment",
			showSwitch: false,
			showSkipComment: true,
			commentAiAssist: true,
			modalContent: getSafeCandidateRating(ctx.containerRatingsService.draft).selectedOBs,
			modalBtnConfirmText: "Save",
			modalBtnCancelText: "Cancel",
			disableClose: true,
			newComment: buildObCommentModalNewComment(ctx, true),
		},
	});

	dialogRef.afterClosed().subscribe((result: { close?: boolean; comment?: string; skipped?: boolean } | undefined) => {
		if (result?.close && (result.skipped || String(result.comment ?? "").trim())) {
			persistObDraft(ctx, control, ctx.obLabel, true, next, result.skipped ? "" : (result.comment ?? ""));
		}
	});
}

export function handleObRemove(ctx: Ts1RatingObContext, control: AbstractControl, current: OBAttempt): void {
	const next = computeObRemoveValue(current);
	if (!next) return;

	if (!ctx.obCommentsEnabled) {
		persistObDraft(ctx, control, ctx.obLabel, false, next, "");
		return;
	}

	const dialogRef = ctx.dialog.open(CbtaModalComponent, {
		panelClass: "ob-comments-modal",
		height: "auto",
		maxHeight: "90vh",
		width: "1154px",
		hasBackdrop: true,
		data: {
			modalName: "obCommentsModel",
			modalTitle: "Comment",
			showSwitch: false,
			showSkipComment: true,
			commentAiAssist: true,
			modalContent: getSafeCandidateRating(ctx.containerRatingsService.draft).selectedOBs,
			modalBtnConfirmText: "Save",
			modalBtnCancelText: "Cancel",
			disableClose: true,
			newComment: buildObCommentModalNewComment(ctx, false),
		},
	});

	dialogRef.afterClosed().subscribe((result: { close?: boolean; comment?: string; skipped?: boolean } | undefined) => {
		if (result?.close && (result.skipped || String(result.comment ?? "").trim())) {
			persistObDraft(ctx, control, ctx.obLabel, false, next, result.skipped ? "" : (result.comment ?? ""));
		}
	});
}

function observationCommentCellMatches(
	row: Pick<OBComments, "candidateId" | "maneuverCode" | "observationCode" | "candidateRole" | "isObservationComment">,
	candidateId: string,
	maneuverCode: string,
	observationCode: string,
	candidateRole: CandidateRole,
): boolean {
	if (!row.isObservationComment) return false;
	if (row.candidateId !== candidateId || row.maneuverCode !== maneuverCode) return false;
	if ((row.observationCode ?? "") !== observationCode) return false;
	return obEventMatchesRole(row, candidateRole);
}

function observationCommentReqMatches(
	row: OBCommentsReq,
	candidateId: string,
	maneuverCode: string,
	observationCode: string,
	candidateRole: CandidateRole,
): boolean {
	if (!row.isObservationComment) return false;
	if (row.candidateId !== candidateId || row.maneuverCode !== maneuverCode) return false;
	if (row.observationCode !== observationCode) return false;
	return obEventMatchesRole(row, candidateRole);
}

export function findActiveObservationComment(
	selectedOBs: OBComments[],
	candidateId: string,
	maneuverCode: string,
	observationCode: string,
	candidateRole: CandidateRole,
): OBComments | undefined {
	for (let i = selectedOBs.length - 1; i >= 0; i--) {
		const row = selectedOBs[i];
		if (observationCommentCellMatches(row, candidateId, maneuverCode, observationCode, candidateRole)) {
			return row;
		}
	}
	return undefined;
}

/** Upsert a standalone Maneuver→OBS observation note without changing +/- counts. */
export function persistObservationCommentDraft(
	ctx: Ts1RatingObContext,
	control: AbstractControl,
	comment: string,
): void {
	const text = comment.trim().toUpperCase();
	if (!text) return;

	const candidateId = ctx.maneuverForm.get("candidateId")?.value;
	const maneuverCode = ctx.maneuverForm.get("maneuverCode")?.value;
	if (!candidateId || !maneuverCode) return;

	const current = control.value as OBAttempt;
	const observationCode = buildObservationCode(ctx.obLabel.toLowerCase());
	const cr = getSafeCandidateRating(ctx.containerRatingsService.draft);
	const createdAt = new Date().toISOString();
	const mLabel = maneuverLabel(ctx.maneuverForm);

	const obAction: OBComments = {
		maneuver: mLabel,
		ob: ctx.obLabel,
		action: null,
		instructorComment: text,
		commentId: "",
		maneuverCode,
		observationCode,
		candidateId,
		candidateRole: ctx.candidateRole,
		isDisabled: false,
		createdAt,
		isObservationComment: true,
	};

	const obActionReq: OBCommentsReq = {
		candidateId,
		observationCode,
		maneuverCode,
		isRemoved: null,
		description: text,
		activeAttempt: current.currentCount ?? 0,
		totalAttempt: current.totalCount ?? 0,
		candidateRole: ctx.candidateRole,
		isDisabled: false,
		createdAt,
		isObservationComment: true,
	};

	const selectedOBs = cr.selectedOBs.filter(
		(row) => !observationCommentCellMatches(row, candidateId, maneuverCode, observationCode, ctx.candidateRole),
	);
	selectedOBs.push(obAction);

	const addRemoveOb = cr.addRemoveOb.filter(
		(row) => !observationCommentReqMatches(row, candidateId, maneuverCode, observationCode, ctx.candidateRole),
	);
	addRemoveOb.push(obActionReq);

	const pendingCandidateRating: CandidateRating = {
		...cr,
		addRemoveOb,
		selectedOBs,
	};

	ctx.containerRatingsService.updateSelectedDraft({
		candidateRating: pendingCandidateRating,
		lastUpdatedDate: new Date().toISOString(),
		continuePage: 2,
	});

	syncManeuverSelectedOBs(ctx, pendingCandidateRating.selectedOBs);

	ctx.onCommentEmitted?.(obAction);
	ctx.onDraftUpdated?.();
}

export function handleObObservationComment(ctx: Ts1RatingObContext, control: AbstractControl): void {
	const candidateId = ctx.maneuverForm.get("candidateId")?.value;
	const maneuverCode = ctx.maneuverForm.get("maneuverCode")?.value;
	if (!candidateId || !maneuverCode) return;

	const observationCode = buildObservationCode(ctx.obLabel.toLowerCase());
	const cr = getSafeCandidateRating(ctx.containerRatingsService.draft);
	const existing = findActiveObservationComment(
		cr.selectedOBs,
		candidateId,
		maneuverCode,
		observationCode,
		ctx.candidateRole,
	);

	const dialogRef = ctx.dialog.open(CbtaModalComponent, {
		panelClass: "ob-comments-modal",
		height: "auto",
		maxHeight: "90vh",
		width: "1154px",
		hasBackdrop: true,
		data: {
			modalName: "obCommentsModel",
			modalTitle: "Comment",
			showSwitch: false,
			showSkipComment: false,
			commentAiAssist: true,
			modalContent: cr.selectedOBs,
			modalBtnConfirmText: "Save",
			modalBtnCancelText: "Cancel",
			disableClose: true,
			initialObComment: existing?.instructorComment ?? "",
			newComment: buildObCommentModalNewComment(ctx, null, true),
		},
	});

	dialogRef.afterClosed().subscribe((result: { close?: boolean; comment?: string; skipped?: boolean } | undefined) => {
		if (result?.close && !result.skipped && String(result.comment ?? "").trim()) {
			persistObservationCommentDraft(ctx, control, result.comment ?? "");
		}
	});
}

export interface ManeuverRepeatContext {
	maneuverForm: UntypedFormGroup;
	initialSnapshot: Record<string, unknown>;
	containerRatingsService: ContainerRatingsService;
	onCommentEmitted?: (comment: OBComments) => void;
	/** v2 nested pf/pm — presence means restore both role columns; omit for legacy flat maneuver forms. */
	roleSlot?: ObRoleSlot;
	candidateRole?: CandidateRole;
	/** TS-1 v3 batch repeat — summary rebuilt once after all columns reset. */
	suppressSummaryEmit?: boolean;
	/** When true, only this candidate's draft rows and outcomes are marked pre-repeat. */
	candidateScoped?: boolean;
}

interface Ts1V3ManeuverSummarySlot {
	candidateIndex: number;
	candidateId: string;
	candidateRole: CandidateRole;
}

/** TS-1 v3 Step 2: summary rows for one maneuver sorted by event history (OB comments + REPEAT). */
export function buildTs1V3ManeuverDisplaySummaryRows(params: {
	selectedOBs: OBComments[];
	addRemoveOb: OBCommentsReq[];
	resetManeuvers: ResetManeuverItem[];
	maneuverCode: string;
	maneuverLabel: string;
	slots: Ts1V3ManeuverSummarySlot[];
	repeatCountByCandidateId: Record<string, number>;
	maneuverOutcomes?: ManeuverOutcomeDraft[];
}): OBComments[] {
	return buildTs1V3ManeuverTimelineRows({
		...params,
		maneuverOutcomes: params.maneuverOutcomes ?? [],
		includeRepeatMarkers: true,
	});
}

function isManeuverOutcomeNaCascadeDescription(description?: string | null): boolean {
	return (
		description === CONSTANT.MANEUVER_OUTCOME_NA_CASCADE_NOTES ||
		description === CONSTANT.RESET_MANEUVER_OUTCOME_NA_CASCADE_NOTES
	);
}

/**
 * Drop Maneuver Outcome N/A cascade/reset rows from the draft event log.
 * Column N/A lives on maneuverOutcomes; cascade is expanded only at audit persist.
 */
export function compactTs1V3CascadeAddRemoveRows<T extends OBCommentsReq | Record<string, unknown>>(rows: T[]): T[] {
	if (!Array.isArray(rows) || rows.length === 0) return rows;
	return rows.filter((row) => {
		const description = String((row as { description?: unknown }).description ?? "");
		return !isManeuverOutcomeNaCascadeDescription(description);
	});
}

/** Resolve outcome storage slot for a summary seat (candidate index + crew role). */
function outcomeSlotForSummarySeat(
	candidateIndex: number,
	candidateRole: CandidateRole,
	hasSecondCandidate: boolean,
): OutcomeSlot {
	const side = candidateIndex === 0 ? "left" : "right";
	return outcomeSlotForObColumn(side, candidateRole, hasSecondCandidate);
}

function isManeuverOutcomeNaForSeat(params: {
	maneuverOutcomes: ManeuverOutcomeDraft[];
	maneuverCode: string;
	candidateIndex: number;
	candidateRole: CandidateRole;
	hasSecondCandidate: boolean;
}): boolean {
	const outcome = params.maneuverOutcomes.find((o) => o.maneuverCode === params.maneuverCode);
	if (!outcome) return false;
	const slot = outcomeSlotForSummarySeat(params.candidateIndex, params.candidateRole, params.hasSecondCandidate);
	return outcome[slot] === "na";
}

function shouldHideObNaSummaryRow(params: {
	row: Pick<
		OBComments,
		| "isManeuverOutcome"
		| "isTemCompetencyOutcome"
		| "isDisabled"
		| "lastActionDisabled"
		| "isObservationComment"
		| "candidateId"
		| "candidateRole"
		| "maneuverCode"
	>;
	slots: Ts1V3ManeuverSummarySlot[];
	maneuverOutcomes: ManeuverOutcomeDraft[];
	maneuverCode: string;
}): boolean {
	const { row, slots, maneuverOutcomes, maneuverCode } = params;
	if (row.isManeuverOutcome || row.isTemCompetencyOutcome || row.isObservationComment) return false;
	if (!row.isDisabled && !row.lastActionDisabled) return false;

	const seat = slots.find(
		(slot) =>
			slot.candidateId === row.candidateId &&
			(!row.candidateRole || row.candidateRole === CandidateRole.NONE || slot.candidateRole === row.candidateRole),
	);
	if (!seat) return false;

	return isManeuverOutcomeNaForSeat({
		maneuverOutcomes,
		maneuverCode,
		candidateIndex: seat.candidateIndex,
		candidateRole: seat.candidateRole,
		hasSecondCandidate: slots.length > 1,
	});
}

/** Active Step 2 summary — preserve selectedOBs append order (OB + Maneuver Outcome interleaved). */
function buildTs1V3ManeuverActiveSummaryRows(
	selectedOBs: OBComments[],
	addRemoveOb: OBCommentsReq[],
	maneuverCode: string,
	slots: Ts1V3ManeuverSummarySlot[],
	maneuverOutcomes: ManeuverOutcomeDraft[] = [],
): OBComments[] {
	const roleByCandidateId = new Map(slots.map((slot) => [slot.candidateId, slot.candidateRole]));
	const candidateIds = new Set(slots.map((slot) => slot.candidateId));
	const rows: OBComments[] = [];
	const selected = selectedOBs ?? [];
	const latestOutcomeIdx = buildLatestActiveOutcomeIndexMap(selected);
	const undoneEventKeys = buildUndoneAddRemoveEventKeys(addRemoveOb, maneuverCode);

	for (let i = 0; i < selected.length; i++) {
		const row = selected[i];
		if (row.maneuverCode !== maneuverCode) continue;
		if (row.isReset || (row.isRepeat ?? false) || row.ob === "TEM" || row.ob === "REPEAT") continue;
		if (!row.isManeuverOutcome && row.candidateId && !candidateIds.has(row.candidateId)) continue;
		if (isReplaceableActiveOutcomeRow(row)) {
			const key = outcomeUpsertKey(row);
			if (key && latestOutcomeIdx.get(key) !== i) continue;
		}
		if (
			shouldHideObNaSummaryRow({
				row,
				slots,
				maneuverOutcomes,
				maneuverCode,
			})
		) {
			continue;
		}
		// Drop orphaned +/- / note rows whose addRemove event was undone (isRepeat).
		if (!row.isManeuverOutcome && !row.isTemCompetencyOutcome && isSelectedObRowUndone(row, undoneEventKeys)) {
			continue;
		}

		rows.push({
			...row,
			candidateRole: row.candidateRole ?? roleByCandidateId.get(row.candidateId ?? "") ?? CandidateRole.NONE,
		});
	}

	return rows;
}

/** Keys for addRemove events marked undone via OB undo (isRepeat), scoped to one maneuver. */
function buildUndoneAddRemoveEventKeys(addRemoveOb: OBCommentsReq[], maneuverCode: string): Set<string> {
	const keys = new Set<string>();
	for (const event of addRemoveOb ?? []) {
		if (event.maneuverCode !== maneuverCode || !event.isRepeat || event.isReset) continue;
		keys.add(selectedObEventDedupeKey(event));
	}
	return keys;
}

function selectedObEventDedupeKey(
	row: Pick<
		OBComments | OBCommentsReq,
		"candidateId" | "maneuverCode" | "observationCode" | "candidateRole" | "createdAt"
	>,
): string {
	return [
		String(row.candidateId ?? ""),
		String(row.maneuverCode ?? ""),
		String(row.observationCode ?? "").toLowerCase(),
		String(row.candidateRole ?? ""),
		String(row.createdAt ?? ""),
	].join("::");
}

function isSelectedObRowUndone(row: OBComments, undoneEventKeys: Set<string>): boolean {
	if (!row.createdAt || undoneEventKeys.size === 0) return false;
	return undoneEventKeys.has(selectedObEventDedupeKey(row));
}

function resolveSummaryRowTimestamp(
	row: OBComments,
	selectedOBs: OBComments[],
	maneuverCode: string,
	fallbackOrder: number,
): number {
	if (row.createdAt) {
		const parsed = new Date(row.createdAt).getTime();
		if (!Number.isNaN(parsed)) return parsed;
	}

	const rowIndex = (selectedOBs ?? []).indexOf(row);
	if (rowIndex >= 0) {
		for (let i = rowIndex - 1; i >= 0; i--) {
			const prev = selectedOBs[i];
			if (prev.maneuverCode !== maneuverCode || !prev.createdAt) continue;
			const t = new Date(prev.createdAt).getTime();
			if (!Number.isNaN(t)) return t + 1;
		}
		for (let i = rowIndex + 1; i < selectedOBs.length; i++) {
			const next = selectedOBs[i];
			if (next.maneuverCode !== maneuverCode || !next.createdAt) continue;
			const t = new Date(next.createdAt).getTime();
			if (!Number.isNaN(t)) return t - 1;
		}
	}

	return fallbackOrder;
}

type Ts1V3TimelineEntry =
	| { kind: "ob"; at: number; order: number; row: OBComments }
	| { kind: "repeat"; at: number; order: number; repeatCount: number; candidateId?: string };

function observationCodeToObLabel(observationCode: string): string {
	const { cat, num } = parseObsCode(observationCode);
	if (!cat) return observationCode;
	return num != null ? `${cat} ${num}` : cat;
}

function findSelectedObCommentForEvent(selectedOBs: OBComments[], event: OBCommentsReq): OBComments | undefined {
	const obLabel = observationCodeToObLabel(event.observationCode);
	const matches = (selectedOBs ?? []).filter(
		(s) =>
			!s.isManeuverOutcome &&
			!s.isReset &&
			s.candidateId === event.candidateId &&
			s.maneuverCode === event.maneuverCode &&
			(s.observationCode === event.observationCode || s.ob === obLabel) &&
			obEventMatchesRole(s, event.candidateRole),
	);
	return matches.find((s) => s.createdAt === event.createdAt);
}

function obSummaryRowFromAddRemoveEvent(
	event: OBCommentsReq,
	maneuverLabel: string,
	selectedOBs: OBComments[],
	resetManeuvers: ResetManeuverItem[] = [],
): OBComments {
	const matched = findSelectedObCommentForEvent(selectedOBs, event);
	const obLabel = observationCodeToObLabel(event.observationCode);
	const action =
		matched?.action ?? (event.isRemoved === null || event.isRemoved === undefined ? null : !event.isRemoved);

	const row: OBComments = {
		maneuver: matched?.maneuver ?? maneuverLabel,
		ob: obLabel,
		action,
		instructorComment: matched?.instructorComment ?? event.description ?? "",
		candidateId: event.candidateId,
		maneuverCode: event.maneuverCode,
		observationCode: event.observationCode,
		candidateRole: event.candidateRole,
		isDisabled: event.isDisabled,
		isRepeat: event.isRepeat,
		isObservationComment: event.isObservationComment === true || matched?.isObservationComment === true,
		createdAt: event.createdAt,
	};

	return withTs1SummaryManeuverLabel(row, resetManeuvers, maneuverLabel);
}

function repeatTimelineEventsForManeuver(
	resetManeuvers: ResetManeuverItem[],
	maneuverCode: string,
): Array<{ repeatCount: number; at: number; candidateId?: string }> {
	const legacyByCount = new Map<number, number>();
	const events: Array<{ repeatCount: number; at: number; candidateId?: string }> = [];

	for (const rm of resetManeuvers ?? []) {
		if (rm.maneuverCode !== maneuverCode) continue;
		const at = new Date(rm.createdAt).getTime();
		if (rm.candidateScoped) {
			events.push({ repeatCount: rm.count, at, candidateId: rm.candidateId });
			continue;
		}
		const prev = legacyByCount.get(rm.count);
		legacyByCount.set(rm.count, prev == null ? at : Math.max(prev, at));
	}

	for (const [repeatCount, at] of legacyByCount.entries()) {
		events.push({ repeatCount, at });
	}

	return events.sort((a, b) => a.at - b.at);
}

/**
 * `isRepeat` is set both when a maneuver is Repeated (history) and when a single OB is undone.
 * Pre-repeat history has createdAt at/before a maneuver reset; post-repeat undos do not.
 */
function isPreManeuverRepeatHistoryEvent(
	createdAt: string | undefined,
	maneuverCode: string,
	resetManeuvers: ResetManeuverItem[],
	candidateId?: string,
): boolean {
	if (!createdAt) return false;
	const eventAt = new Date(createdAt).getTime();
	if (Number.isNaN(eventAt)) return false;
	return (resetManeuvers ?? []).some((rm) => {
		if (rm.maneuverCode !== maneuverCode) return false;
		if (rm.candidateScoped && candidateId && rm.candidateId !== candidateId) return false;
		return new Date(rm.createdAt).getTime() >= eventAt;
	});
}

function buildTs1V3ManeuverTimelineRows(params: {
	selectedOBs: OBComments[];
	addRemoveOb: OBCommentsReq[];
	resetManeuvers: ResetManeuverItem[];
	maneuverCode: string;
	maneuverLabel: string;
	slots: Ts1V3ManeuverSummarySlot[];
	repeatCountByCandidateId: Record<string, number>;
	includeRepeatMarkers: boolean;
	maneuverOutcomes?: ManeuverOutcomeDraft[];
}): OBComments[] {
	const {
		selectedOBs,
		addRemoveOb,
		resetManeuvers,
		maneuverCode,
		maneuverLabel,
		slots,
		includeRepeatMarkers,
		maneuverOutcomes = [],
	} = params;
	const roleByCandidateId = new Map(slots.map((slot) => [slot.candidateId, slot.candidateRole]));
	const timeline: Ts1V3TimelineEntry[] = [];
	let order = 0;
	const latestOutcomeIdx = buildLatestActiveOutcomeIndexMap(selectedOBs ?? []);
	const hasSecondCandidate = slots.length > 1;

	for (const event of addRemoveOb ?? []) {
		if (event.maneuverCode !== maneuverCode || event.isReset) continue;
		if (event.isRepeat) {
			// isRepeat marks both pre-maneuver-repeat history and per-OB undos.
			// Only keep history superseded by a maneuver Repeat — hide post-repeat undos.
			if (!includeRepeatMarkers) continue;
			if (!isPreManeuverRepeatHistoryEvent(event.createdAt, maneuverCode, resetManeuvers, event.candidateId)) {
				continue;
			}
		}
		// Cascade N/A from Maneuver Outcome — keep for A/B calc, hide from instructor comments.
		if (isManeuverOutcomeNaCascadeDescription(event.description)) continue;

		const seat = slots.find(
			(slot) =>
				slot.candidateId === event.candidateId &&
				(!event.candidateRole ||
					event.candidateRole === CandidateRole.NONE ||
					slot.candidateRole === event.candidateRole),
		);
		if (
			seat &&
			(event.isDisabled ||
				event.description === CONSTANT.NOT_APPLICABLE_NOTES ||
				event.description === CONSTANT.RESET_NOT_APPLICABLE_NOTES) &&
			isManeuverOutcomeNaForSeat({
				maneuverOutcomes,
				maneuverCode,
				candidateIndex: seat.candidateIndex,
				candidateRole: seat.candidateRole,
				hasSecondCandidate,
			})
		) {
			continue;
		}

		if (event.description === CONSTANT.RESET_NOT_APPLICABLE_NOTES) {
			const eventAt = new Date(event.createdAt).getTime();
			for (let i = timeline.length - 1; i >= 0; i--) {
				const prev = timeline[i];
				if (prev.kind !== "ob") continue;
				if (prev.row.candidateId !== event.candidateId) continue;
				if (Boolean(prev.row.isRepeat) !== Boolean(event.isRepeat)) break;
				if (
					(resetManeuvers ?? []).some((rm) => {
						if (rm.maneuverCode !== maneuverCode) return false;
						if (rm.candidateScoped && rm.candidateId && rm.candidateId !== event.candidateId) {
							return false;
						}
						const rmAt = new Date(rm.createdAt).getTime();
						return rmAt >= prev.at && rmAt < eventAt;
					})
				) {
					break;
				}
				if (
					prev.row.isObservationComment ||
					prev.row.isManeuverOutcome ||
					prev.row.isTemCompetencyOutcome ||
					!prev.row.isDisabled
				) {
					continue;
				}
				if (prev.row.observationCode !== event.observationCode) continue;
				if (!obEventMatchesRole(prev.row, event.candidateRole)) continue;
				timeline.splice(i, 1);
				break;
			}
			continue;
		}

		timeline.push({
			kind: "ob",
			at: new Date(event.createdAt).getTime(),
			order: order++,
			row: obSummaryRowFromAddRemoveEvent(event, maneuverLabel, selectedOBs, resetManeuvers),
		});
	}

	for (let i = 0; i < (selectedOBs ?? []).length; i++) {
		const row = selectedOBs[i];
		if (!row.isManeuverOutcome || row.isReset || row.maneuverCode !== maneuverCode) continue;
		if (row.isRepeat ?? false) {
			if (!includeRepeatMarkers) continue;
			if (!isPreManeuverRepeatHistoryEvent(row.createdAt, maneuverCode, resetManeuvers, row.candidateId)) {
				continue;
			}
		}
		if (isReplaceableActiveOutcomeRow(row)) {
			const key = outcomeUpsertKey(row);
			if (key && latestOutcomeIdx.get(key) !== i) continue;
		}
		timeline.push({
			kind: "ob",
			at: resolveSummaryRowTimestamp(row, selectedOBs, maneuverCode, order),
			order: order++,
			row: withTs1SummaryManeuverLabel(
				{
					...row,
					candidateRole: row.candidateRole ?? roleByCandidateId.get(row.candidateId ?? "") ?? CandidateRole.NONE,
				},
				resetManeuvers,
				maneuverLabel,
			),
		});
	}

	if (includeRepeatMarkers) {
		for (const repeatEvent of repeatTimelineEventsForManeuver(resetManeuvers, maneuverCode)) {
			timeline.push({
				kind: "repeat",
				at: repeatEvent.at,
				order: order++,
				repeatCount: repeatEvent.repeatCount,
				candidateId: repeatEvent.candidateId,
			});
		}
	}

	timeline.sort((a, b) => a.at - b.at || a.order - b.order);

	return timeline.map((entry) =>
		entry.kind === "repeat"
			? buildTs1V3ManeuverRepeatRow(
					maneuverCode,
					maneuverLabel,
					entry.repeatCount,
					new Date(entry.at).toISOString(),
					entry.candidateId,
				)
			: entry.row,
	);
}

function ts1V3ActiveSummaryRowKey(row: OBComments): string {
	return `${row.candidateId ?? ""}::${row.maneuverCode ?? ""}::${row.observationCode ?? row.ob}::${row.candidateRole ?? ""}`;
}

/**
 * Merge draft selectedOBs with live maneuver-form selectedOBs.
 * Form rows win for active OBs — avoids losing PM (or PF) rows when async draft writes race.
 */
export function mergeTs1V3SelectedOBsForSummary(
	ratingForm: UntypedFormArray,
	draftSelected: OBComments[],
): OBComments[] {
	const formActiveByKey = new Map<string, OBComments>();

	for (let ci = 0; ci < ratingForm.length; ci++) {
		const obs = ratingForm.at(ci).get("observations") as UntypedFormArray | null;
		if (!obs?.length) continue;

		for (let mi = 0; mi < obs.length; mi++) {
			const form = obs.at(mi) as UntypedFormGroup;
			const rows = (form.get("selectedOBs")?.value ?? []) as OBComments[];
			for (const row of rows) {
				if (row.isRepeat || row.isReset || row.isManeuverOutcome || row.ob === "TEM" || row.ob === "REPEAT") {
					continue;
				}
				formActiveByKey.set(ts1V3ActiveSummaryRowKey(row), row);
			}
		}
	}

	const merged: OBComments[] = [];
	const seenActive = new Set<string>();

	for (const row of draftSelected ?? []) {
		if (row.isReset || row.ob === "TEM" || row.ob === "REPEAT") continue;

		if (row.isManeuverOutcome) {
			if (!(row.isRepeat ?? false)) {
				merged.push(row);
			}
			continue;
		}

		if (row.isRepeat) {
			merged.push(row);
			continue;
		}

		const key = ts1V3ActiveSummaryRowKey(row);
		const live = formActiveByKey.get(key);
		merged.push(live ?? row);
		seenActive.add(key);
	}

	for (const [key, row] of formActiveByKey) {
		if (!seenActive.has(key)) {
			merged.push(row);
		}
	}

	return merged;
}

function buildTs1V3ManeuverRepeatRow(
	maneuverCode: string,
	maneuverLabel: string,
	repeatCount: number,
	createdAt: string,
	candidateId?: string,
): OBComments {
	return {
		maneuver: `R${repeatCount} ${maneuverLabel}`.trim(),
		ob: "REPEAT",
		remarks: "REPEAT",
		maneuverCode,
		candidateId,
		repeatCount,
		action: false,
		instructorComment: "",
		isDisabled: false,
		createdAt,
	} as OBComments;
}

/** Chronological summary order — matches Step 3 Review (createdAt ascending, stable tie-break). */
export function sortTs1V3SummaryCommentsByCreatedAt(comments: OBComments[]): void {
	const indexed = comments.map((row, index) => ({ row, index }));
	indexed.sort((a, b) => {
		const ta = a.row.createdAt ? new Date(a.row.createdAt).getTime() : 0;
		const tb = b.row.createdAt ? new Date(b.row.createdAt).getTime() : 0;
		if (ta !== tb) return ta - tb;
		return a.index - b.index;
	});
	comments.length = 0;
	comments.push(...indexed.map((entry) => entry.row));
}

/** TS-1 v3 Step 2 only — group summary by maneuver with one REPEAT marker per maneuver. */
export function syncTs1V3RatingSummaryFromManeuverForms(
	obComments: OBComments[],
	ratingForm: UntypedFormArray,
	draftSelected: OBComments[],
	crewRoles: CandidateRole[],
	addRemoveOb: OBCommentsReq[] = [],
	resetManeuvers: ResetManeuverItem[] = [],
	maneuverOutcomes: ManeuverOutcomeDraft[] = [],
): void {
	obComments.length = 0;
	if (!ratingForm?.length) return;

	const obs0 = ratingForm.at(0)?.get("observations") as UntypedFormArray | null;
	if (!obs0?.length) return;

	const maneuverSlots: Array<{ index: number; maneuverCode: string; maneuverLabel: string }> = [];
	for (let mi = 0; mi < obs0.length; mi++) {
		const refForm = obs0.at(mi) as UntypedFormGroup;
		const maneuverCode = refForm.get("maneuverCode")?.value as string;
		const maneuverLabel = (refForm.get("maneuver")?.value ?? "") as string;
		if (!maneuverCode || isCompetencyManeuverCode(maneuverCode)) continue;
		maneuverSlots.push({ index: mi, maneuverCode, maneuverLabel });
	}

	// Index once — cascade N/A can make addRemoveOb large; avoid O(maneuvers × events) scans.
	const addRemoveByManeuver = groupByManeuverCode(addRemoveOb);
	const selectedByManeuver = groupByManeuverCode(draftSelected);
	const resetsByManeuver = groupByManeuverCode(resetManeuvers);

	for (const { index: mi, maneuverCode, maneuverLabel } of maneuverSlots) {
		const selectedOBs = selectedByManeuver.get(maneuverCode) ?? [];
		const maneuverAddRemove = addRemoveByManeuver.get(maneuverCode) ?? [];
		const maneuverResets = resetsByManeuver.get(maneuverCode) ?? [];

		const slots: Ts1V3ManeuverSummarySlot[] = [];
		const repeatCountByCandidateId: Record<string, number> = {};

		for (let ci = 0; ci < ratingForm.length; ci++) {
			const obs = ratingForm.at(ci).get("observations") as UntypedFormArray | null;
			const form = obs?.at(mi) as UntypedFormGroup | undefined;
			if (!form) continue;

			const candidateId = form.get("candidateId")?.value as string;
			if (!candidateId) continue;

			slots.push({
				candidateIndex: ci,
				candidateId,
				candidateRole: crewRoles[ci] ?? CandidateRole.PF,
			});
			repeatCountByCandidateId[candidateId] = Number(form.get("repeat")?.value ?? 0);
		}

		const hasRepeatHistory =
			Object.values(repeatCountByCandidateId).some((count) => count > 0) ||
			slots.some((slot) => maneuverHasRepeatSummaryHistory(selectedOBs, slot.candidateId, maneuverCode));

		const displayRows = hasRepeatHistory
			? buildTs1V3ManeuverDisplaySummaryRows({
					selectedOBs,
					addRemoveOb: maneuverAddRemove,
					resetManeuvers: maneuverResets,
					maneuverCode,
					maneuverLabel,
					slots,
					repeatCountByCandidateId,
					maneuverOutcomes,
				})
			: buildTs1V3ManeuverActiveSummaryRows(selectedOBs, maneuverAddRemove, maneuverCode, slots, maneuverOutcomes);

		for (const row of displayRows) {
			appendObCommentToSummary(obComments, row);
		}
	}

	sortTs1V3SummaryCommentsByCreatedAt(obComments);
}

function groupByManeuverCode<T extends { maneuverCode?: string | null }>(
	rows: T[] | null | undefined,
): Map<string, T[]> {
	const byCode = new Map<string, T[]>();
	for (const row of rows ?? []) {
		const code = row.maneuverCode;
		if (!code) continue;
		const list = byCode.get(code);
		if (list) list.push(row);
		else byCode.set(code, [row]);
	}
	return byCode;
}

/** Step 2 display summary filtered to one candidate (Step 3 Review carousel). */
export function buildTs1V3CandidateSummaryComments(params: {
	ratingForm: UntypedFormArray;
	draftSelected: OBComments[];
	crewRoles: CandidateRole[];
	addRemoveOb?: OBCommentsReq[];
	resetManeuvers?: ResetManeuverItem[];
	maneuverOutcomes?: ManeuverOutcomeDraft[];
	candidateId: string;
}): OBComments[] {
	const { ratingForm, draftSelected, crewRoles, candidateId } = params;
	const addRemoveOb = params.addRemoveOb ?? [];
	const resetManeuvers = params.resetManeuvers ?? [];
	const maneuverOutcomes = params.maneuverOutcomes ?? [];

	const all: OBComments[] = [];
	syncTs1V3RatingSummaryFromManeuverForms(
		all,
		ratingForm,
		draftSelected,
		crewRoles,
		addRemoveOb,
		resetManeuvers,
		maneuverOutcomes,
	);

	const synced = all.filter((row) => {
		if (row.candidateId === candidateId) return true;
		if (row.ob === "REPEAT" && row.maneuverCode) {
			if (row.candidateId) return row.candidateId === candidateId;
			return resetManeuvers.some((rm) => rm.maneuverCode === row.maneuverCode && !rm.candidateScoped);
		}
		return false;
	});

	const temExtras = draftSelected.filter((row) => row.isTemCompetencyOutcome && row.candidateId === candidateId);

	const merged = [...synced, ...temExtras];
	const latestOutcomeIdx = buildLatestActiveOutcomeIndexMap(merged);
	const deduped = merged.filter((row, index) => {
		if (!isReplaceableActiveOutcomeRow(row)) return true;
		const key = outcomeUpsertKey(row);
		return !key || latestOutcomeIdx.get(key) === index;
	});
	sortTs1V3SummaryCommentsByCreatedAt(deduped);

	return deduped.map((row) => {
		if (row.isTemCompetencyOutcome || row.ob === "REPEAT") return row;
		return withTs1SummaryManeuverLabel(row, resetManeuvers);
	});
}

/** Dedupe left/right Repeat targets by form identity (single-candidate PF/PM share one form). */
export function uniqueManeuverFormsForRepeat<T extends { form: UntypedFormGroup | null | undefined }>(
	targets: T[],
): Array<T & { form: UntypedFormGroup }> {
	const seen = new Set<UntypedFormGroup>();
	const unique: Array<T & { form: UntypedFormGroup }> = [];
	for (const target of targets) {
		const form = target.form;
		if (!form || seen.has(form)) continue;
		seen.add(form);
		unique.push({ ...target, form });
	}
	return unique;
}

/** Reset one maneuver form to its session-start snapshot and record a repeat in draft + summary. */
export function applyManeuverRepeat(ctx: ManeuverRepeatContext): void {
	const form = ctx.maneuverForm;
	const candidateId = form.get("candidateId")?.value;
	const maneuverCode = form.get("maneuverCode")?.value;
	const maneuverLabel = form.get("maneuver")?.value ?? "";
	const assessmentId = form.get("assessmentId")?.value;
	const traineeAssessmentId = form.get("traineeAssessmentId")?.value;
	if (!candidateId || !maneuverCode) return;

	const newRepeat = Number(form.get("repeat")?.getRawValue() ?? 0) + 1;

	if (ctx.roleSlot) {
		// v3 nested forms: maneuver-wide Repeat clears both PF and PM (covers role-swap history).
		restoreManeuverFormRoleFromSnapshot(form, ctx.initialSnapshot, "pf");
		restoreManeuverFormRoleFromSnapshot(form, ctx.initialSnapshot, "pm");
	} else {
		form.setValue(structuredClone(ctx.initialSnapshot));
	}
	form.get("repeat")?.patchValue(newRepeat);

	const draft = ctx.containerRatingsService.draft;
	if (!ctx.containerRatingsService.inDraftMode && !draft) return;

	const cr = getSafeCandidateRating(draft);

	const candidateScoped = ctx.candidateScoped === true;

	const matchesRepeatScope = (row: {
		candidateId?: string;
		maneuverCode?: string;
		isManeuverOutcome?: boolean;
		ob?: string;
	}): boolean => {
		if (!row.candidateId || !row.maneuverCode) return false;
		if (row.maneuverCode !== maneuverCode) return false;
		if (row.ob === "REPEAT" || row.ob === "TEM") return false;
		if (row.isManeuverOutcome) {
			return candidateScoped ? row.candidateId === candidateId : true;
		}
		return row.candidateId === candidateId;
	};

	const filteredAddRemoveOb = cr.addRemoveOb.map((a) => (matchesRepeatScope(a) ? { ...a, isRepeat: true } : a));

	const filteredSelectedOBs = cr.selectedOBs.map((a) => (matchesRepeatScope(a) ? { ...a, isRepeat: true } : a));

	const temRate = { ...cr.temRate };
	if (candidateId && assessmentId) {
		delete temRate[`${candidateId}.${assessmentId}`];
	}

	const resetManeuvers = [
		...cr.resetManeuvers,
		{
			traineeAssessmentId,
			maneuverCode,
			candidateId,
			comment: "",
			count: newRepeat,
			createdAt: new Date().toISOString(),
			...(candidateScoped ? { candidateScoped: true } : {}),
		},
	];

	const pendingCandidateRating: CandidateRating = {
		...cr,
		resetManeuvers,
		temRate,
		addRemoveOb: filteredAddRemoveOb,
		selectedOBs: filteredSelectedOBs,
	};

	ctx.containerRatingsService.updateSelectedDraft({
		candidateRating: pendingCandidateRating,
		lastUpdatedDate: new Date().toISOString(),
		continuePage: 2,
	});

	const maneuverSummaryRows = filterSelectedOBsForSummary(pendingCandidateRating.selectedOBs, candidateId).filter(
		(s) => s.maneuverCode === maneuverCode,
	);
	form.get("selectedOBs")?.patchValue(maneuverSummaryRows, { emitEvent: false });

	if (!ctx.suppressSummaryEmit) {
		const displayRows = buildTs1V3ManeuverDisplaySummaryRows({
			selectedOBs: pendingCandidateRating.selectedOBs,
			addRemoveOb: pendingCandidateRating.addRemoveOb,
			resetManeuvers: pendingCandidateRating.resetManeuvers,
			maneuverCode,
			maneuverLabel,
			slots: [{ candidateIndex: 0, candidateId, candidateRole: ctx.candidateRole ?? CandidateRole.NONE }],
			repeatCountByCandidateId: { [candidateId]: newRepeat },
		});

		ctx.onCommentEmitted?.({
			isReset: true,
			syncComments: displayRows,
			maneuverCode,
			candidateId,
		} as OBComments);
	}
}

/** Strip v3-only draft fields before API staging/complete payloads. */
export function stripV3DraftOnlyFields(draft: DraftItem): DraftItem {
	if (!draft?.candidateRating) return draft;
	const snapshots = rebuildDisplaySnapshots(
		draft.candidateRating,
		collectAssessmentConfigObPresets(draft.formDetails?.assessmentConfig),
	);
	const {
		crewRoleAssignment: _c,
		maneuverOutcomes: _m,
		temCompetencyOutcomes: _t,
		obCommentsEnabled: _o,
		...candidateRating
	} = snapshots;
	return {
		...draft,
		candidateRating: {
			...candidateRating,
			addRemoveOb: compactTs1V3CascadeAddRemoveRows(candidateRating.addRemoveOb ?? []),
			displayComments: snapshots.displayComments,
			displayObs: snapshots.displayObs,
		},
	};
}
